import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { toast } from '@/components/toast';
import { useUpdateAgent } from '@/pages/agents/api';
import { getAgentLanguage, useCompareEditStates, useGetCurrentAgentsPresenter } from '@/pages/agents/hooks';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { AgentPageMode, useClearEditor } from '@/state/agent';
import { isImageProcessing } from '@/state/presenters/presenters';
import { useAgentEditorStore } from '@/state/root';
import { AppRoutes, ExtendedLocation } from '@/utils/types';
import { AgentCreateTabType, AgentKnowledgeSource, Presenter, VideoType } from '@d-id/types';
import equal from 'fast-deep-equal';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { AGENT_INSTRUCTION_MAX_LENGTH, AGENT_TEXT_KNOWLEDGE_MAX_LENGTH, MAX_FILES_UPLOAD } from '../../../constants';
import { getAgentBgObject, validateAgentGrounding } from '../../../utils';
import { HeaderButton } from './common';

const EditButton = observer(() => {
    const agentEditorStore = useAgentEditorStore();
    const [unknownError, setUnknownError] = useState(false);
    const presenter: Presenter | undefined = useGetCurrentAgentsPresenter();
    const isLoadingPresenter = useRecoilValue(isImageProcessing);
    const navigate = useNavigate();
    const mixpanel = useAnalytics();
    const { clearEditor } = useClearEditor();
    const { isAnonymous, loginWithRedirect } = useAuth();
    const { isLoading, mutateAsync: updateAgentMutation } = useUpdateAgent();
    const locationState = useLocation() as ExtendedLocation;

    const { wasEmbedded } = locationState.state || {};
    const agent = agentEditorStore.inputAgent;
    const { getIsAgentStateChanged } = useCompareEditStates();
    const isEdited = getIsAgentStateChanged();

    useEffect(() => {
        agentEditorStore.setPageMode(AgentPageMode.Edit);
    }, [agentEditorStore.pageMode]);

    const disabled = useMemo(
        () =>
            !isEdited ||
            agentEditorStore.isStarterQuestionsEdited ||
            isLoadingPresenter ||
            isLoading ||
            (agentEditorStore.llm.instructions?.length ?? 0) > AGENT_INSTRUCTION_MAX_LENGTH ||
            (agentEditorStore.llm.prompt_customization?.knowledge_source == AgentKnowledgeSource.BaseKnowledge &&
                (agentEditorStore.baseKnowledge?.length ?? 0) > AGENT_TEXT_KNOWLEDGE_MAX_LENGTH) ||
            agentEditorStore.isUpdatingKnowledge,
        [
            isLoadingPresenter,
            isLoading,
            agentEditorStore.llm.instructions,
            agentEditorStore.llm.prompt_customization?.knowledge_source,
            agentEditorStore.baseKnowledge?.length,
            agentEditorStore.isUpdatingKnowledge,
            agentEditorStore.uploadingDocumentFiles,
            agentEditorStore.isStarterQuestionsEdited,
            agentEditorStore.presenterBackgroundColor,
            agentEditorStore.presenterBackgroundThumbnailUrl,
            agentEditorStore.presenterBackgroundSourceUrl,
            isEdited,
        ]
    );

    const validateAgentFields = () => {
        if (!validateAgentGrounding()) {
            return false;
        }
        if (unknownError) {
            toast.error({ title: `Unknown error occur!`, toastId: 'agent-edit-unknown-error' });
            return false;
        }

        return true;
    };

    const getIsUpdatePresenter = () => {
        const {
            ttsSelectedVoice,
            initAgentState: { presenter_id },
            presenterIsStitch,
            presenterBackgroundSourceUrl,
            presenterBackgroundColor,
        } = agentEditorStore;
        return (
            (ttsSelectedVoice?.id &&
                (ttsSelectedVoice.id !== agent?.presenter?.voice?.voice_id ||
                    ttsSelectedVoice?.language !== agent?.presenter?.voice?.language)) ||
            (presenter?.id && presenter_id !== presenter?.id) ||
            (agent?.presenter?.type === VideoType.TALK && agent?.presenter?.stitch !== presenterIsStitch) ||
            presenterBackgroundSourceUrl ||
            presenterBackgroundColor
        );
    };

    const handleEdit = async () => {
        const {
            inputAgent: { preview_name },
            ttsSelectedVoice,
            ttsSelectedLanguage,
            uploadingDocumentFiles,
            deletedDocumentFiles,
            greetings,
            llm,
            triggers,
            starterQuestions,
            presenterIsStitch,
            setIsCreating,
            presenterBackgroundSourceUrl,
            presenterBackgroundThumbnailUrl,
            presenterBackgroundColor,
        } = agentEditorStore;
        const mixpanelData = {
            agentId: agent?.id,
            presenter: presenter?.url,
            driver: presenter?.driverId,
            type: presenter?.type,
            presenterType: presenter?.presenterType,
            presenterLabels: presenter?.labels,
            isDefault: presenter?.isDefault,
            agentName: name,
            selectedVoice: ttsSelectedVoice,
            documentLength: uploadingDocumentFiles.length,
            deletedDocumentLength: deletedDocumentFiles.length,
            agentGroundingType: llm.template,
            presenterId: presenter?.id,
            presenterBackgroundSourceUrl,
            presenterBackgroundThumbnailUrl,
        };
        mixpanel?.track('agent-edit', {
            event: 'click',
            ...mixpanelData,
        });

        if (isAnonymous) {
            mixpanel?.track('go-to-signup', { event: 'click', from: 'agent-document-create', isAnonymous });
            loginWithRedirect({
                appState: { returnTo: window.location.origin + AppRoutes.PostSso + '?from=agent' },
            });
            return;
        }

        if (!agent?.id || !agent?.knowledge?.id || !presenter) {
            return;
        }

        if (!validateAgentFields()) {
            return;
        }

        try {
            agentEditorStore.setIsCreating(true);

            const update: any = { embed: wasEmbedded ?? agent.embed, triggers, llm: { ...llm } };
            if (preview_name?.trim() !== agent.preview_name) update.preview_name = preview_name?.trim();

            const instructionsUpdated = llm.instructions?.trim() !== agent.llm?.instructions;
            const groundingStateUpdated = llm.template !== agent.llm?.template;
            if (instructionsUpdated || groundingStateUpdated) {
                if (instructionsUpdated) update.llm.instructions = llm.instructions?.trim();
                if (groundingStateUpdated) update.llm.template = llm.template;
            }

            if (getIsUpdatePresenter()) {
                const voice = !ttsSelectedVoice?.id
                    ? agent.presenter?.voice
                    : {
                          type: ttsSelectedVoice?.provider,
                          voice_id: ttsSelectedVoice?.id,
                          access: ttsSelectedVoice?.access,
                          language: getAgentLanguage(ttsSelectedVoice, ttsSelectedLanguage),
                      };
                update.preview_thumbnail = presenterBackgroundThumbnailUrl || presenter.thumbnail || presenter.poster;

                const presenterCommonInfo = {
                    voice,
                    thumbnail: presenterBackgroundThumbnailUrl || presenter?.thumbnail || presenter?.poster,
                };

                if (presenter?.type === 'clip') {
                    update.presenter = {
                        ...presenterCommonInfo,
                        type: presenter.type,
                        presenter_id: presenter.id,
                        driver_id: presenter?.driverId,
                        ...getAgentBgObject(VideoType.CLIP, presenterBackgroundColor, presenterBackgroundSourceUrl),
                    };
                } else {
                    update.presenter = {
                        ...presenterCommonInfo,
                        presenter_id: presenter.id,
                        type: 'talk',
                        source_url: presenterBackgroundSourceUrl || presenter.poster,
                        stitch: presenterIsStitch,
                        ...getAgentBgObject(VideoType.TALK, presenterBackgroundColor, presenterBackgroundSourceUrl),
                    };
                }
            }
            if (greetings.length > 0 && !equal(greetings[0], agent.greetings?.[0])) update.greetings = greetings;
            await updateAgentMutation({
                update,
                agentId: agent.id,
                knowledgeId: agent.knowledge.id,
                uploadingFiles: uploadingDocumentFiles,
                deletedFiles: deletedDocumentFiles,
                ...(!equal(starterQuestions, agent.starter_message) ? { starter_message: starterQuestions } : {}),
            });

            mixpanel?.track('agent-edit', {
                event: 'success',
                ...mixpanelData,
            });

            clearEditor();
            setIsCreating(false);
            navigate(AppRoutes.Agent, {
                state: { resetState: true, isAfterGenerate: true },
            });
            toast.success({ title: `Agent updated successfully`, toastId: 'agent-edit-success' });
        } catch (error) {
            mixpanel?.track('agent-edit', {
                event: 'error',
                ...mixpanelData,
            });
            setIsCreating(false);
        }
    };

    useEffect(() => {
        if (agentEditorStore.uploadingDocumentFiles.length > MAX_FILES_UPLOAD) {
            toast.error({ title: `Cannot upload more than ${MAX_FILES_UPLOAD} documents`, toastId: 'agent-generate-too-many-documents' });
            agentEditorStore.setErrorTabs([AgentCreateTabType.KnowledgeBase]);
        }
    }, [agentEditorStore.uploadingDocumentFiles]);

    return (
        <>
            <ErrorBoundary
                fallback={() => {
                    setUnknownError(true);
                    return <></>;
                }}
                beforeCapture={scope => scope.setTag('component', 'EditButton')}>
                <HeaderButton
                    variant="contained"
                    color="secondary"
                    onClick={handleEdit}
                    aria-label="Edit"
                    disabled={disabled}
                    isLoading={agentEditorStore.isCreating}>
                    Update agent
                </HeaderButton>
            </ErrorBoundary>
        </>
    );
});

export default EditButton;
